import type { AppValueFormat } from "./catalog";

/** Shown for a missing value. DESIGN.md: empty fields read as an em dash, never "null". */
export const EMPTY_VALUE = "—";

const NUMERIC_FORMATS: ReadonlySet<AppValueFormat> = new Set([
  "number",
  "currency",
  "percent",
]);

/** Number-like formats right-align and use tabular digits. */
export function isNumericFormat(format: AppValueFormat | undefined): boolean {
  return format !== undefined && NUMERIC_FORMATS.has(format);
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Accept ISO strings and epoch millis. A bare `YYYY-MM-DD` is a calendar date
 * (DATEONLY), so it is pinned to UTC and formatted in UTC — otherwise users
 * west of Greenwich see the previous day.
 */
export function toDate(
  value: unknown,
): { date: Date; dateOnly: boolean } | null {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(`${value}T00:00:00Z`);
    return Number.isNaN(date.getTime()) ? null : { date, dateOnly: true };
  }
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : { date, dateOnly: false };
  }
  return null;
}

export interface FormatOptions {
  currency?: string;
  /** BCP 47 locale; undefined uses the runtime default (the viewer's browser). */
  locale?: string;
}

function isScalar(value: unknown): value is string | number | boolean {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

/**
 * People read yes/no; "true" is a programmer's word. Labels follow the App's
 * languages (those its creator notices come in), English otherwise.
 */
const BOOLEAN_LABELS: Readonly<Record<string, readonly [string, string]>> = {
  en: ["Yes", "No"],
  es: ["Sí", "No"],
  fr: ["Oui", "Non"],
};

function language(locale: string | undefined): string {
  try {
    return new Intl.Locale(
      locale ?? new Intl.NumberFormat().resolvedOptions().locale,
    ).language;
  } catch {
    return "en";
  }
}

function booleanText(value: boolean, locale: string | undefined): string {
  const [yes, no] = BOOLEAN_LABELS[language(locale)] ?? ["Yes", "No"];
  return value ? yes : no;
}

function scalarText(
  value: string | number | boolean | bigint,
  locale: string | undefined,
): string {
  return typeof value === "boolean"
    ? booleanText(value, locale)
    : String(value);
}

function jsonText(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    // Circular or otherwise unserializable: show something, never throw.
    return Object.prototype.toString.call(value);
  }
}

function plain(value: unknown, options: FormatOptions = {}): string {
  if (isScalar(value) || typeof value === "bigint")
    return scalarText(value, options.locale);
  // A list of plain values reads as a list ("a, b"), not as JSON.
  if (Array.isArray(value) && value.every(isScalar)) {
    return value.map((item) => scalarText(item, options.locale)).join(", ");
  }
  return jsonText(value);
}

type Formatter = (value: unknown, options: FormatOptions) => string;

/**
 * Intl formatters are expensive to build and a table renders thousands of
 * cells, so each (locale, options) pair is built once and reused.
 */
const intlCache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();

function cachedNumberFormat(
  locale: string | undefined,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const key = `n|${locale ?? ""}|${JSON.stringify(options)}`;
  const hit = intlCache.get(key);
  if (hit instanceof Intl.NumberFormat) return hit;
  const created = new Intl.NumberFormat(locale, options);
  intlCache.set(key, created);
  return created;
}

function cachedDateFormat(
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `d|${locale ?? ""}|${JSON.stringify(options)}`;
  const hit = intlCache.get(key);
  if (hit instanceof Intl.DateTimeFormat) return hit;
  const created = new Intl.DateTimeFormat(locale, options);
  intlCache.set(key, created);
  return created;
}

/** Format a number-like value, or fall back to its plain text. */
function numeric(
  toOptions: (options: FormatOptions) => Intl.NumberFormatOptions,
): Formatter {
  return (value, options) => {
    const n = toNumber(value);
    return n === null
      ? plain(value, options)
      : cachedNumberFormat(options.locale, toOptions(options)).format(n);
  };
}

function temporal(withTime: boolean): Formatter {
  return (value, options) => {
    const parsed = toDate(value);
    if (!parsed) return plain(value, options);
    return cachedDateFormat(options.locale, {
      dateStyle: "medium",
      ...(withTime && !parsed.dateOnly ? { timeStyle: "short" } : {}),
      ...(parsed.dateOnly ? { timeZone: "UTC" } : {}),
    }).format(parsed.date);
  };
}

const FORMATTERS: Record<AppValueFormat, Formatter> = {
  text: plain,
  mono: plain,
  badge: plain,
  number: numeric(() => ({})),
  currency: numeric((options) => ({
    style: "currency",
    currency: options.currency ?? "USD",
  })),
  percent: numeric(() => ({ style: "percent", maximumFractionDigits: 1 })),
  date: temporal(false),
  datetime: temporal(true),
};

/**
 * Format a value for display. Never throws: a value that does not fit its
 * format (a word in a currency column) falls back to its plain text, because
 * an App view must degrade, not crash, when its data drifts.
 */
export function formatAppValue(
  value: unknown,
  format: AppValueFormat = "text",
  options: FormatOptions = {},
): string {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;
  if (Array.isArray(value) && value.length === 0) return EMPTY_VALUE;
  try {
    return FORMATTERS[format](value, options);
  } catch {
    // An invalid locale or currency code throws from Intl.
    return plain(value);
  }
}

/** Format a ratio delta with an explicit sign: 0.042 → "+4.2%". */
export function formatDelta(delta: number, locale?: string): string {
  return cachedNumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
  }).format(delta);
}

export type StatusTone =
  "default" | "secondary" | "outline" | "success" | "warning" | "destructive";

const STATUS_TONES: ReadonlyArray<[RegExp, StatusTone]> = [
  [
    /^(paid|approved|complete(d)?|done|active|matched|received|shipped|success(ful)?|ok)$/i,
    "success",
  ],
  [
    /^(pending|review|in[ _-]?review|on[ _-]?hold|partial(ly)?[ _-]?\w*|waiting|scheduled|due)$/i,
    "warning",
  ],
  [
    /^(failed|error|rejected|overdue|cancel(l)?ed|void(ed)?|blocked|exception|late)$/i,
    "destructive",
  ],
];

/**
 * Pick a badge tone for a status word, so a `format: badge` column colors
 * itself. Unknown statuses stay neutral (`secondary`): color must signal
 * meaning, and guessing would make it decorative.
 */
export function statusTone(value: unknown): StatusTone {
  const text = typeof value === "string" ? value.trim() : "";
  for (const [pattern, tone] of STATUS_TONES) {
    if (pattern.test(text)) return tone;
  }
  return "secondary";
}
